import SearchableMultiSelectController from "./searchable_multi_select_controller"

const MIN_SEARCH_LENGTH = 2
const SEARCH_DELAY_MS = 300

// The license type admin's Add Classifications picker: a multiple <select>
// shown as a slim-select whose options are searched on the server as you type,
// so any number of classifications -- of any type and code length -- can be
// chosen before the form is submitted. Loading every classification up front
// would mean thousands of options, so none are rendered with the page.
//
// slim-select v1's ajax mode (slimselect.js:947-977) hands each keystroke to
// the ajax callback; an array answer becomes the option list, a string answer
// is shown as a message. Chosen options are carried into every new list
// (:743-750), so they stay selected -- and submitted -- across searches; this
// controller leaves them out of the results so they are not listed twice.
//
// The tab stop, combobox role, label, keyboard handling and Turbo-cache
// teardown are inherited from searchable_multi_select_controller.js.
//
// Usage:
//   <div data-controller="license-type-classification-picker"
//        data-license-type-classification-picker-url="/license_types/7/classification_options.json"
//        data-license-type-classification-picker-placeholder="Search by code or name">
//     <label for="classification_ids">Add classifications</label>
//     <select id="classification_ids" name="license_type_classification[classification_ids][]" multiple
//             data-target="license-type-classification-picker.select"></select>
//   </div>
export default class extends SearchableMultiSelectController {
  slimSelectOptions() {
    return {
      ...super.slimSelectOptions(),
      closeOnSelect: false,
      searchPlaceholder: "Code or name",
      searchText: "No classifications found.",
      searchingText: "Searching…",
      ajax: (search, answer) => this.search(search, answer)
    }
  }

  // Waits for typing to pause, then asks the server. Only the latest text is
  // answered: a debounced search is simply never sent, and every keystroke
  // invalidates any request already in flight, so its response is dropped even
  // if it lands during the pause or after the text is cut below the minimum.
  search(text, answer) {
    this.invalidateRequests()
    const query = text.trim()
    if (query.length < MIN_SEARCH_LENGTH) {
      answer(`Type at least ${MIN_SEARCH_LENGTH} characters to search.`)
      return
    }

    this.searchTimer = setTimeout(() => this.fetchOptions(query, answer), SEARCH_DELAY_MS)
  }

  invalidateRequests() {
    clearTimeout(this.searchTimer)
    this.latestRequest = (this.latestRequest || 0) + 1
  }

  fetchOptions(query, answer) {
    const request = this.latestRequest
    const isLatest = () => request === this.latestRequest

    fetch(this.searchUrl(query), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Classification search failed: ${response.status}`)
        return response.json()
      })
      .then((options) => { if (isLatest()) answer(this.unchosen(options)) })
      .catch(() => { if (isLatest()) answer("Classifications could not be loaded. Try again.") })
  }

  searchUrl(query) {
    return `${this.data.get("url")}?search=${encodeURIComponent(query)}`
  }

  // slim-select compares option values as strings.
  unchosen(options) {
    const chosen = new Set(this.chosen().map((option) => option.value))
    return options
      .map((option) => ({ value: String(option.value), text: option.text }))
      .filter((option) => !chosen.has(option.value))
  }

  teardown() {
    this.invalidateRequests()
    super.teardown()
  }
}
